// JanetRail — the persistent Janet interface of the shared shell, and the
// ONLY Janet entry point while the shell is on (the top bar's Ask Janet bar
// and Chat button, the floating Janet button and the Janet nav tab step
// aside for it).
//
// Mounted ONCE by the layout (not by each page), so it stays put and keeps
// its conversation while the viewer moves between the dashboard and module
// workspaces. It wraps the same Janet docks Home already uses
// (HomeJanetDockDesktop / HomeJanetDockMobile → JanetChatPanel): no second
// chat engine, and nothing sends or listens until a person taps inside it.
// What Janet is focused on comes from Janet's own page context, set by the
// workspace (useWorkspace) — the desktop panel shows it in its header chip;
// the condensed bar shows it on its own line.
import { useEffect, useRef } from 'react'
import { Sparkles } from 'lucide-react'
import { HomeJanetDockDesktop, HomeJanetDockMobile } from '../home/HomeJanetDock'
import { useJanetPage } from '../../contexts/JanetPageContext'
import { useJanetUnread, useMarkVisibleTopicRead } from '../../hooks/useJanetUnread'

// Desktop (≥lg): Janet recedes until she is needed (Aaron, 2026-10-07: "she
// should become more visible when engaged and recede into the background while
// the materials are reviewed"). Receded, she is a slim strip on the right edge
// with her mark and what she is looking at; one click, ⌥Space, or any "Ask
// Janet" door brings the full column forward. While forward, a click in the
// work area lets her recede again — unless she is busy (a message in flight,
// a half-typed question, voice on), which the panel reports on its root as
// data-janet-busy. The panel stays mounted while receded, so the conversation,
// a draft in the composer and a streaming reply all survive.
export function JanetRailDesktop() {
  const { page, isOpen, openJanet, closeJanet, pickTopic, activeSessionId } = useJanetPage()
  const asideRef = useRef(null)
  // Conversations Janet started and you haven't opened: the strip says so,
  // and a click opens the newest of them.
  const unread = useJanetUnread()
  // The docked panel stays mounted while receded; a topic is read once she's open.
  useMarkVisibleTopicRead(activeSessionId, isOpen)
  const summon = () => {
    if (unread.latestId) pickTopic(unread.latestId)
    openJanet()
  }

  useEffect(() => {
    if (!isOpen) return undefined
    const onDown = (e) => {
      const aside = asideRef.current
      if (!aside || aside.contains(e.target)) return
      // A dialog or menu opened from Janet (topics, a draft) lives outside the
      // column in a portal; clicks there are still conversation.
      if (e.target.closest?.('[role="dialog"], [role="menu"], [data-janet-keep]')) return
      if (aside.querySelector('[data-janet-busy]')) return
      closeJanet()
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [isOpen, closeJanet])

  return (
    <aside
      ref={asideRef}
      className="hidden lg:flex flex-col shrink-0 border-l min-h-0 transition-[width] duration-200 ease-out"
      style={{ width: isOpen ? 'clamp(330px, 23vw, 420px)' : 44, borderColor: 'var(--ns-hair-soft)', background: isOpen ? 'white' : 'var(--ns-paper, #fbfaf7)' }}
      aria-label="Janet"
      data-testid="janet-rail"
      data-open={isOpen ? '1' : '0'}
    >
      {!isOpen && (
        <button
          type="button"
          onClick={summon}
          className="group flex-1 flex flex-col items-center pt-3 gap-3 w-full hover:bg-janet-50 transition-colors"
          title={unread.count ? `Janet started ${unread.count} conversation${unread.count === 1 ? '' : 's'}${unread.latestTitle ? ` — ${unread.latestTitle}` : ''}` : 'Ask Janet (⌥Space)'}
          aria-label={unread.count ? `Janet has ${unread.count} new` : 'Ask Janet'}
          data-testid="janet-rail-summon"
        >
          <span className="relative w-8 h-8 rounded-full flex items-center justify-center bg-janet-100 text-janet-700 group-hover:bg-janet-600 group-hover:text-white transition-colors">
            <Sparkles size={15} />
            {unread.count > 0 && (
              <span data-testid="janet-unread" className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-janet-700 text-white text-[10px] font-bold leading-4 text-center">{unread.count}</span>
            )}
          </span>
          <span className="text-[11px] font-medium tracking-wide text-janet-700/80 whitespace-nowrap" style={{ writingMode: 'vertical-rl' }}>
            {unread.count ? `Janet has ${unread.count} new for you` : page?.entityName ? `Ask Janet about ${page.entityName}` : 'Ask Janet'}
          </span>
        </button>
      )}
      <div className={isOpen ? 'flex-1 min-h-0 flex flex-col' : 'hidden'}>
        <HomeJanetDockDesktop onClose={closeJanet} />
      </div>
    </aside>
  )
}

// Narrow widths: the condensed bar, expandable in place — same component,
// same conversation — with what Janet is looking at.
export function JanetRailCompact() {
  const { page } = useJanetPage()
  return (
    <section className="lg:hidden" aria-label="Janet">
      {page?.entityName && (
        <div className="mb-1.5 flex items-center gap-1.5 text-[11px] min-w-0" style={{ color: 'var(--ns-gray)' }}>
          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: 'var(--ns-janet)' }} />
          <span className="shrink-0">Janet is looking at</span>
          <span className="font-semibold truncate" style={{ color: 'var(--ns-janet)' }}>{page.entityType === 'demo_example' ? 'Demo example' : page.entityType} · {page.entityName}</span>
        </div>
      )}
      <div className="[&>div]:mb-0"><HomeJanetDockMobile /></div>
    </section>
  )
}
